<template>
    <section class="ei-list-panel">
        <div class="ei-list-search">
            <i class="fas fa-search ei-search-icon"></i>
            <input
                v-model.trim="filters.keyword"
                class="ei-search-input"
                type="text"
                placeholder="코드 또는 이름 검색"
                @keyup.enter="loadList(1)"
            />
            <button
                v-if="filters.keyword"
                type="button"
                class="ei-search-clear"
                @click="
                    filters.keyword = '';
                    loadList(1);
                "
            >
                <i class="fas fa-times"></i>
            </button>
        </div>

        <div class="ei-list-filters">
            <select v-model="filters.enIntCategory" class="ei-filter-select" @change="loadList(1)">
                <option value="">전체 카테고리</option>
                <option v-for="c in enIntCategoryItems" :key="c.value" :value="c.value">
                    {{ c.text || c.value }}
                </option>
            </select>
            <div class="ei-filter-chips">
                <button
                    v-for="f in statusFilters"
                    :key="f.value"
                    type="button"
                    class="ei-filter-chip"
                    :class="{ on: filters.enabled === f.value }"
                    @click="
                        filters.enabled = f.value;
                        loadList(1);
                    "
                >
                    {{ f.label }}
                </button>
            </div>
        </div>

        <div v-if="loading" class="ei-list-loading"><i class="fas fa-spinner fa-spin"></i> 조회 중…</div>
        <div v-else class="ei-list-body">
            <div
                v-for="row in rows"
                :key="row.enIntIdx"
                class="ei-list-item"
                :class="{ selected: selectedEnIntIdx === row.enIntIdx }"
                @click="onRowActivate(row.enIntIdx)"
            >
                <div class="ei-item-top">
                    <div class="ei-item-info">
                        <span class="ei-item-name">{{ row.enIntName }}</span>
                        <span class="ei-item-code">{{ row.enIntCode }}</span>
                    </div>
                    <CustomDropdown class="ei-item-dropdown" :items="buildItemMenu(row)" align="right">
                        <template #trigger>
                            <i class="fas fa-ellipsis-v ei-item-menu-icon"></i>
                        </template>
                    </CustomDropdown>
                </div>
                <div class="ei-item-meta">
                    <span class="ei-badge ei-badge--cat">{{
                        categoryName(row.enIntCategory) || row.enIntCategory
                    }}</span>
                    <span class="ei-badge" :class="row.enabled === 'Y' ? 'ei-badge--on' : 'ei-badge--off'">
                        {{ row.enabled === "Y" ? "활성" : "비활성" }}
                    </span>
                    <span v-if="row.calcEnabled === 'Y'" class="ei-badge ei-badge--calc">
                        {{ row.intensityUnit }}
                    </span>
                </div>
            </div>

            <div v-if="!rows.length" class="ei-list-empty">
                <i class="fas fa-inbox"></i>
                <strong>조건에 맞는 항목이 없습니다</strong>
            </div>
        </div>

        <div v-if="!loading" class="ei-list-footer">
            <span>{{ rows.length }} / {{ total }}건</span>
            <div class="ei-list-footer-right">
                <button type="button" class="ei-list-new-btn" @click="$emit('new')">
                    <i class="fas fa-plus"></i> 신규 등록
                </button>
                <div v-if="totalPages > 1" class="ei-pager-btns">
                    <button type="button" class="ei-pager-btn" :disabled="page <= 1" @click="loadList(page - 1)">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="ei-pager-current">{{ page }} / {{ totalPages }}</span>
                    <button
                        type="button"
                        class="ei-pager-btn"
                        :disabled="page >= totalPages"
                        @click="loadList(page + 1)"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import CustomDropdown from "@components/dropdown/CustomDropdown.vue";
import { mapEnIntCategoryState, ensureCommonCodes, categoryNameByValue } from "../utils/enIntUi.js";

export default {
    name: "EnIntListPanel",
    components: { CustomDropdown },
    props: {
        selectedEnIntIdx: { type: Number, default: null },
    },
    computed: {
        ...mapEnIntCategoryState(),
        totalPages() {
            return Math.max(1, Math.ceil(this.total / this.pageSize) || 1);
        },
    },
    data() {
        return {
            loading: false,
            rows: [],
            total: 0,
            page: 1,
            pageSize: 30,
            filters: { enIntCategory: "", enabled: "", keyword: "" },
            statusFilters: [
                { value: "", label: "전체" },
                { value: "Y", label: "활성" },
                { value: "N", label: "비활성" },
            ],
        };
    },
    async mounted() {
        await ensureCommonCodes(this.$store);
        await this.loadList(1);
    },
    methods: {
        /** 상위에서 선택·calcList 등 한 번에 처리하도록 알림 */
        onRowActivate(enIntIdx) {
            this.$emit("select", enIntIdx);
            this.$emit("selection-change", { enIntIdx });
        },
        categoryName(code) {
            return categoryNameByValue(this.enIntCategoryItems, code);
        },
        buildItemMenu(row) {
            const self = this;
            return [
                {
                    label: "수정",
                    icon: "fa-pen",
                    onClick() {
                        self.$emit("edit", row.enIntIdx);
                    },
                },
                {
                    label: "삭제",
                    icon: "fa-trash-alt",
                    variant: "danger",
                    onClick() {
                        self.confirmDeleteMaster(row.enIntIdx);
                    },
                },
            ];
        },
        async confirmDeleteMaster(enIntIdx) {
            const confirmed = await this.alertConfirmWarning("연관 데이터가 삭제됩니다. 계속하시겠습니까?");
            if (!confirmed.value) return;
            try {
                const deleteMaster_response = await backEndApi.enInt.deleteMaster({ enIntIdx });
                const deleteMaster_data = deleteMaster_response?.data ?? null;
                if (deleteMaster_response.status !== 200) throw new Error(deleteMaster_data?.detail);
                this.alertNoti("처리되었습니다.");
                await this.refresh();
                this.$emit("deleted", enIntIdx);
            } catch (error) {
                console.error("삭제에러", error);
                this.alertDanger(` ${error.message}`);
            }
        },
        async loadList(p) {
            this.loading = true;
            try {
                const params = { page: p, pageSize: this.pageSize };
                if (this.filters.enIntCategory) params.enIntCategory = this.filters.enIntCategory;
                if (this.filters.enabled) params.enabled = this.filters.enabled;
                if (this.filters.keyword) params.keyword = this.filters.keyword;
                const getMasterList_response = await backEndApi.enInt.getMasterList(params);
                const getMasterList_data = getMasterList_response?.data ?? null;
                this.rows = Array.isArray(getMasterList_data?.rows) ? getMasterList_data.rows : [];
                this.total =
                    typeof getMasterList_data?.total === "number" ? getMasterList_data.total : this.rows.length;
                this.page = typeof getMasterList_data?.page === "number" ? getMasterList_data.page : p;
            } catch {
                this.rows = [];
                this.total = 0;
                this.page = p;
                this.alertNoti("목록을 불러오지 못했습니다.");
            } finally {
                this.loading = false;
            }
        },
        async refresh() {
            await this.loadList(this.page);
        },
    },
};
</script>

<style scoped>
.ei-list-panel {
    display: flex;
    flex-direction: column;
    border-right: 1px solid rgba(203, 213, 225, 0.84);
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 0.98));
    min-width: 0;
    min-height: 0;
}

.ei-list-search {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 10px 10px 0;
    padding: 0 10px;
    min-height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.94);
    background: rgba(255, 255, 255, 0.92);
    flex-shrink: 0;
}

.ei-search-icon {
    font-size: 11px;
    color: #94a3b8;
    flex-shrink: 0;
}

.ei-search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    font-size: 12px;
    color: #1b263b;
    background: transparent;
}

.ei-search-input::placeholder {
    color: #cbd5e1;
}

.ei-search-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 50%;
    background: rgba(148, 163, 184, 0.16);
    color: #64748b;
    cursor: pointer;
}

.ei-search-clear:hover {
    background: rgba(244, 63, 94, 0.12);
    color: #f43f5e;
}

.ei-list-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px 10px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.92);
    flex-shrink: 0;
    flex-wrap: wrap;
}

.ei-filter-select {
    height: 28px;
    padding: 0 8px;
    border: 1px solid rgba(226, 232, 240, 0.94);
    border-radius: 6px;
    background: #fff;
    font-size: 11px;
    font-weight: 600;
    color: #334155;
    outline: none;
}

.ei-filter-chips {
    display: flex;
    gap: 4px;
}

.ei-filter-chip {
    box-sizing: border-box;
    min-height: 24px;
    padding: 3px 9px;
    border-radius: 999px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: rgba(255, 255, 255, 0.85);
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    transition:
        border-color 0.15s,
        color 0.15s,
        background 0.15s;
}

.ei-filter-chip:hover {
    border-color: rgba(148, 163, 184, 0.65);
    color: #334155;
}

.ei-filter-chip.on {
    border-color: rgba(14, 116, 144, 0.3);
    background: rgba(14, 116, 144, 0.08);
    color: #0e7490;
}

.ei-list-loading {
    padding: 28px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
}

.ei-list-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 8px;
}

.ei-list-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 9px 10px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.88);
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    transition:
        border-color 0.15s,
        background 0.15s,
        box-shadow 0.15s;
}

.ei-list-item:hover {
    border-color: rgba(148, 163, 184, 0.72);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.ei-list-item.selected {
    border-color: rgba(14, 116, 144, 0.32);
    background: linear-gradient(180deg, rgba(224, 242, 254, 0.6), rgba(255, 255, 255, 0.98));
    box-shadow: inset 0 0 0 1px rgba(14, 116, 144, 0.06);
}

.ei-item-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px;
}

.ei-item-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.ei-item-name {
    font-size: 12px;
    font-weight: 700;
    color: #1b263b;
    line-height: 1.35;
}

.ei-item-code {
    font-size: 10px;
    color: #94a3b8;
}

.ei-item-dropdown {
    flex-shrink: 0;
}

.ei-item-menu-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 22px;
    border-radius: 4px;
    color: #94a3b8;
    cursor: pointer;
    transition:
        color 0.15s,
        background 0.15s;
}

.ei-item-menu-icon:hover {
    color: #475569;
    background: rgba(241, 245, 249, 0.9);
}

.ei-item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.ei-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 18px;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.ei-badge--cat {
    color: #475569;
    background: rgba(241, 245, 249, 0.95);
}

.ei-badge--on {
    color: #059669;
    background: rgba(5, 150, 105, 0.1);
}

.ei-badge--off {
    color: #64748b;
    background: rgba(148, 163, 184, 0.12);
}

.ei-badge--calc {
    color: #0e7490;
    background: rgba(14, 116, 144, 0.08);
}

.ei-list-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 28px 12px;
    border: 1px dashed rgba(203, 213, 225, 0.95);
    border-radius: 8px;
    background: rgba(248, 250, 252, 0.7);
    text-align: center;
}

.ei-list-empty i {
    font-size: 20px;
    color: #cbd5e1;
}

.ei-list-empty strong {
    font-size: 12px;
    color: #64748b;
}

.ei-list-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 7px 10px;
    border-top: 1px solid rgba(241, 245, 249, 0.94);
    background: rgba(248, 250, 252, 0.82);
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.ei-list-footer-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ei-list-new-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid rgba(14, 116, 144, 0.25);
    background: rgba(14, 116, 144, 0.06);
    color: #0e7490;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.15s,
        border-color 0.15s;
    white-space: nowrap;
}

.ei-list-new-btn:hover {
    background: rgba(14, 116, 144, 0.12);
    border-color: rgba(14, 116, 144, 0.35);
}

.ei-pager-btns {
    display: flex;
    align-items: center;
    gap: 4px;
}

.ei-pager-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 6px;
    background: #fff;
    color: #64748b;
    font-size: 10px;
    cursor: pointer;
}

.ei-pager-btn:disabled {
    opacity: 0.4;
    cursor: default;
}

.ei-pager-current {
    font-size: 11px;
    font-weight: 700;
    color: #334155;
    min-width: 36px;
    text-align: center;
}
</style>
